import {
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    AfterViewInit,
    SimpleChanges,
    ViewChild,
    Injectable
} from '@angular/core';
import moment from 'moment';
import { NgbDateAdapter, NgbDateParserFormatter, NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';

/**
 * This Service handles how the date is represented in scripts i.e. ngModel.
 */
@Injectable()
export class CustomAdapter extends NgbDateAdapter<string> {
    readonly DELIMITER = '-';

    fromModel(value: string | null): NgbDateStruct | null {
        if (value) {
            if (moment.isMoment(value)) {
                value = value.format('YYYY-MM-DD');
            }
            const date = value?.split(this.DELIMITER);
            return {
                day: parseInt(date[2], 10),
                month: parseInt(date[1], 10),
                year: parseInt(date[0], 10)
            };
        }
        return null;
    }

    toModel(date: NgbDateStruct | null): string | null {
        // return date ? date.day + this.DELIMITER + date.month + this.DELIMITER + date.year : null;

        if (!date) return null;
        return `${date.year.toString().padStart(4, '0')}${this.DELIMITER}${date.month.toString().padStart(2, '0')}${this.DELIMITER
            }${date.day.toString().padStart(2, '0')}`;
    }
}

/**
 * This Service handles how the date is rendered and parsed from keyboard i.e. in the bound input field.
 */
@Injectable()
export class CustomDateParserFormatter extends NgbDateParserFormatter {
    readonly DELIMITER = '-';

    parse(value: string): NgbDateStruct | null {
        if (value) {
            const date = value.split(this.DELIMITER);
            return {
                day: parseInt(date[0], 10),
                month: parseInt(date[1], 10),
                year: parseInt(date[2], 10)
            };
        }
        return null;
    }

    format(date: NgbDateStruct | null): string {
        // return date ? date.day + this.DELIMITER + date.month + this.DELIMITER + date.year : '';

        if (!date) return null;
        return `${date.day.toString().padStart(2, '0')}${this.DELIMITER}${date.month.toString().padStart(2, '0')}${this.DELIMITER
            }${date.year.toString().padStart(4, '0')}`;
    }
}

@Component({
    selector: 'app-field-date',
    templateUrl: './field-date.component.html',
    styleUrls: ['./field-date.component.scss'],
    providers: [
        { provide: NgbDateAdapter, useClass: CustomAdapter },
        { provide: NgbDateParserFormatter, useClass: CustomDateParserFormatter }
    ],
    standalone: false
})
export class FieldDateComponent implements OnInit, OnChanges, AfterViewInit {
    @Input() label?: string;
    @Input() error?: string;
    @Input() classes: string;
    @Input() tooltipObj?: any = null;
    @Input() optionalString?: string;
    @Input() placeholder?: string;
    @Input() autocomplete: string;
    @Input() model: string;
    @Input() focus: boolean = false;
    @Input() width: string = null;
    @Input() disabled: boolean = false;
    @Input() disabledLabel: boolean = false;
    @Input() disableSince: string = null;
    @Input() disableUntil: string = null;
    @Input() disableOrEquals: boolean = false;
    @Output() modelChange = new EventEmitter();

    calendarIsOpen: boolean = false;
    isFocused: boolean = false;
    invalidDate: boolean = false;
    localModel: string = null;
    locale: string = 'en';

    ready: boolean = false;
    localDisableSince: any = null;
    localDisableUntil: any = null;

    constructor() { }

    @ViewChild('myinput') myInputField: ElementRef;
    ngAfterViewInit() {
        // if (this.focus) {
        //     this.myInputField.nativeElement.focus();
        // }
    }

    ngOnInit(): void {
        // if (this.disableSince) {
        //     const date = moment(this.disableSince).subtract(1, 'days');
        //     if (this.disableOrEquals) date.add(1, 'days');
        //     if (date.isValid()) {
        //         const day = date.date();
        //         const month = date.month() + 1;
        //         const year = date.year();
        //         this.localDisableSince = {
        //             year: year,
        //             month: month,
        //             day: day
        //         };
        //     }
        // }
        // if (this.disableUntil) {
        //     const date = moment(this.disableUntil).add(1, 'days');
        //     if (this.disableOrEquals) date.subtract(1, 'days');
        //     if (date.isValid()) {
        //         const day = date.date();
        //         const month = date.month() + 1;
        //         const year = date.year();
        //         this.localDisableUntil = {
        //             year: year,
        //             month: month,
        //             day: day
        //         };
        //     }
        // }
        this.updateDisabledDates();
        this.ready = true;
    }

    ngOnChanges(changes: SimpleChanges) {
        if (!this.model) {
            this.localModel = null;
            return;
        }
        const date = moment(this.model);
        if (!date.isValid()) return;
        const day = date.date();
        const month = date.month() + 1;
        const year = date.year();
        if (changes.model) {
            this.localModel = this.model;
        }

        this.updateDisabledDates();
        if (changes.disableSince || changes.disableUntil) {
            this.ready = false;
            setTimeout(() => {
                this.ready = true;
            }, 1);
        }
    }

    updateDisabledDates() {
        if (this.disableSince) {
            const date = moment(this.disableSince).subtract(1, 'days');
            if (this.disableOrEquals) date.add(1, 'days');
            if (date.isValid()) {
                const day = date.date();
                const month = date.month() + 1;
                const year = date.year();
                this.localDisableSince = {
                    year: year,
                    month: month,
                    day: day
                };
            }
        }
        if (this.disableUntil) {
            const date = moment(this.disableUntil).add(1, 'days');
            if (this.disableOrEquals) date.subtract(1, 'days');
            if (date.isValid()) {
                const day = date.date();
                const month = date.month() + 1;
                const year = date.year();
                this.localDisableUntil = {
                    year: year,
                    month: month,
                    day: day
                };
            }
        }
    }

    onDateChanged(event) {
        if (!event) {
            this.modelChange.emit(null);
            return;
        }
        const date = `${event.year.toString().padStart(4, '0')}-${event.month.toString().padStart(2, '0')}-${event.day
            .toString()
            .padStart(2, '0')}`;
        if (moment(date).isValid() && this.model !== this.localModel) {
            if (this.model !== this.localModel) this.modelChange.emit(date);
        } else this.modelChange.emit(null);
    }

    invalid() {
        setTimeout(() => {
            this.modelChange.emit(null);
        }, 1);
    }

    onBlur($event?) {
        this.isFocused = false;
        if (this.localModel === this.model) return;
        if (this.localModel && moment(this.localModel).isValid()) {
            if (this.model !== this.localModel) this.modelChange.emit(this.localModel);
        } else this.modelChange.emit(null);
    }

    calendarToggle($event) {
        if ($event == 1) {
            this.calendarIsOpen = true;
        } else this.calendarIsOpen = false;
    }

    onFocus() {
        this.isFocused = true;
    }
}
